import { z } from "zod";
import { localizedTextSchema } from "./common";

/**
 * Localized list of bullet items. Each locale defaults to an empty array so
 * partial fills (only EN, no FR yet) don't fail validation. Items themselves
 * are arbitrary strings — sanitize on the rendering side.
 *
 * es/it/de/nl are optional rather than defaulted so rows written before the
 * four later locales parse unchanged. They must be declared: z.object strips
 * unknown keys, so leaving them out made every admin save throw away the
 * Spanish, Italian, German and Dutch lists of every block.
 */
export const localizedStringListSchema = z.object({
  en: z.array(z.string()).default([]),
  fr: z.array(z.string()).default([]),
  ar: z.array(z.string()).default([]),
  es: z.array(z.string()).optional(),
  it: z.array(z.string()).optional(),
  de: z.array(z.string()).optional(),
  nl: z.array(z.string()).optional(),
});
export type LocalizedStringList = z.infer<typeof localizedStringListSchema>;

/**
 * One stop on a multi-stop itinerary. `title` is required per locale; the
 * description is free-form (rich text not enforced — plain text rendered with
 * line breaks). `durationMinutes` powers the "≈ 45 min" hint on the web.
 */
export const itineraryStopSchema = z.object({
  title: localizedTextSchema,
  description: localizedTextSchema.optional(),
  durationMinutes: z.number().int().nonnegative().nullable().optional(),
  tags: z.array(z.string()).default([]),
  // Optional geo anchor — when ≥2 stops carry coordinates the clients draw
  // the itinerary route on a map (2026-07 "map + timeline" concept).
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  // ── City-guide fields (2026-08). All optional, so `trip` / `event`
  // itineraries written before this are still valid.
  /** Groups stops under "Day 1", "Day 2"… Absent → a single unnamed group. */
  day: z.number().int().positive().nullable().optional(),
  /** "morning", "after lunch", "sunset" — localized, so it can't be a `tag`. */
  when: localizedTextSchema.optional(),
  /** `<city>/<segment-or-category>/<slug>`; the locale is prefixed at render. */
  activityPath: z.string().nullable().optional(),
  /** Thumbnail snapshot for the stop card; refreshed when the batch re-seeds. */
  imageUrl: z.string().nullable().optional(),
});
export type ItineraryStop = z.infer<typeof itineraryStopSchema>;

export const itinerarySchema = z.array(itineraryStopSchema).default([]);

/**
 * "Why people go": a restaurant's three reasons, each an emoji, a short title
 * and one line (owner, 2026-09-28, from /restaurant-why-lab). Unlike the
 * string lists, every item carries three fields, so it gets its own shape:
 * one array of items per locale, the same length and order in every locale.
 * `en` is the only required locale so a partly translated row still parses;
 * the MCP writer (`activity_set_why_go`) is what insists on all seven.
 */
export const whyGoItemSchema = z.object({
  emoji: z.string().min(1).max(16),
  title: z.string().min(1).max(80),
  line: z.string().min(1).max(220),
});
export type WhyGoItem = z.infer<typeof whyGoItemSchema>;

const whyGoListSchema = z.array(whyGoItemSchema).max(4);

export const whyGoSchema = z.object({
  en: whyGoListSchema.default([]),
  fr: whyGoListSchema.optional(),
  ar: whyGoListSchema.optional(),
  es: whyGoListSchema.optional(),
  it: whyGoListSchema.optional(),
  de: whyGoListSchema.optional(),
  nl: whyGoListSchema.optional(),
});
export type WhyGo = z.infer<typeof whyGoSchema>;

/**
 * MVP `experience` template — a small set of structured bullet sections used
 * for Activities / Shows / Pools. Future templates (`trip`, `stay`,
 * `restaurant`) plug into the same registry with their own schemas.
 */
export const experienceBlocksSchema = z.object({
  whyGo: whyGoSchema.optional(),
  highlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  pickupInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  included: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  excluded: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  // Set menus / food served on the experience (dinner shows, pool day passes
  // with a meal). Same key as the `restaurant` template so renderers already
  // know how to display it.
  menuHighlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notSuitable: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notAllowed: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToBring: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  infoBeforeBooking: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  knowBeforeYouGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExperienceBlocks = z.infer<typeof experienceBlocksSchema>;

/**
 * `trip` template — used for Trips / Excursions / Day-trips. Adds a
 * day-by-day itinerary on top of the experience-style bullet sections.
 */
export const tripBlocksSchema = z.object({
  itinerary: itinerarySchema,
  whyGo: whyGoSchema.optional(),
  highlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  pickupInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  included: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  excluded: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notSuitable: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notAllowed: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToBring: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  infoBeforeBooking: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  knowBeforeYouGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type TripBlocks = z.infer<typeof tripBlocksSchema>;

export const stayBlocksSchema = z.object({
  highlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  pickupInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  included: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  excluded: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  propertyDetails: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestFor: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  amenities: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  roomFeatures: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  services: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  safety: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  accessibility: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  checkInOut: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  houseRules: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notSuitable: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notAllowed: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToBring: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  infoBeforeBooking: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  knowBeforeYouGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type StayBlocks = z.infer<typeof stayBlocksSchema>;

export const restaurantBlocksSchema = z.object({
  whyGo: whyGoSchema.optional(),
  highlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  pickupInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  included: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  excluded: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  menuHighlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  dietaryInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  seatingInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notSuitable: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notAllowed: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToBring: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  infoBeforeBooking: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  knowBeforeYouGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type RestaurantBlocks = z.infer<typeof restaurantBlocksSchema>;

/**
 * Explore-mode templates — keyed by `activities.segment` for rows where
 * `mode='explore'`. Each template defines a fixed set of editorial sections
 * (localized bullet lists) the admin renders + Discover displays. Sections
 * skipped when empty.
 */
export const exploreLocationBlocksSchema = z.object({
  whyVisit: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToExpect: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  howToCombine: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  openingHours: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  /**
   * Ticketed monuments (Bahia, Menara, Volubilis, the Ministry of Culture
   * sites) live under this template, and "what does it cost" is the query
   * that actually converts. Without a price block the fee ends up buried in
   * practicalTips where it reads as an aside. Reuses the `blocks.price_range`
   * label that the restaurant/stay/spa templates already ship in all 7
   * locales, so this adds no new i18n keys.
   */
  priceRange: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  practicalTips: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreLocationBlocks = z.infer<typeof exploreLocationBlocksSchema>;

export const exploreRestaurantBlocksSchema = z.object({
  whyEat: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  menuHighlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  howToCombine: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  cuisine: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  priceRange: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  openingHours: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreRestaurantBlocks = z.infer<typeof exploreRestaurantBlocksSchema>;

/**
 * Nightlife: clubs, late bars, dinner-shows.
 *
 * Deliberately NOT the location blocks. A traveller choosing a monument wants
 * "why visit" and "what to expect"; someone choosing a club wants to know what
 * music is played, which night is worth going, how late it runs and whether
 * they will be let in. Filing clubs under explore-location asked the wrong
 * questions of the editor and produced sightseeing copy about a dancefloor.
 */
export const exploreNightlifeBlocksSchema = z.object({
  whyGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  musicAndVibe: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestNights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  openingHours: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  gettingIn: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  priceRange: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  goodToKnow: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreNightlifeBlocks = z.infer<typeof exploreNightlifeBlocksSchema>;

/**
 * Beach and pool day clubs — a DAYTIME product, despite some running evening
 * parties. Kept apart from nightlife on the same reasoning the wider industry
 * uses (TripAdvisor files them under outdoor activities, OSM under
 * leisure=beach_resort): the question is "where do I spend a day by water",
 * and the answer turns on the day pass, the pool and the food, not the DJ.
 */
export const exploreBeachClubBlocksSchema = z.object({
  whyGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToExpect: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  dayPass: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  foodAndDrink: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  openingHours: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  goodToKnow: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreBeachClubBlocks = z.infer<typeof exploreBeachClubBlocksSchema>;

export const exploreStayBlocksSchema = z.object({
  whyStay: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToExpect: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  nearby: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  amenities: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  priceRange: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreStayBlocks = z.infer<typeof exploreStayBlocksSchema>;

export const exploreSpaBlocksSchema = z.object({
  whyBook: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  treatments: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  howToCombine: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  priceRange: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  openingHours: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreSpaBlocks = z.infer<typeof exploreSpaBlocksSchema>;

export const exploreGuideBlocksSchema = z.object({
  overview: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  /**
   * Ordered route stops. Structured (not a bullet list) so the guide page can
   * group them by day, link each one to the venue's own page and show a
   * thumbnail — see
   * docs/superpowers/specs/2026-08-07-guide-itinerary-template-design.md
   */
  stops: itinerarySchema,
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  practicalTips: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  howToCombine: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreGuideBlocks = z.infer<typeof exploreGuideBlocksSchema>;

/**
 * `explore-golf` template — golf courses as editorial explore rows. Deliberately
 * carries NO price block: green fees are seasonal, vary by member/visitor and
 * twilight rate, and rot within a season. Course facts (holes, par, architect)
 * are stable and published by every course, so they are safe to carry.
 */
export const exploreGolfBlocksSchema = z.object({
  whyPlay: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  courseDetails: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  facilities: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  openingHours: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  bestTime: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  practicalTips: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  howToCombine: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type ExploreGolfBlocks = z.infer<typeof exploreGolfBlocksSchema>;

export const eventBlocksSchema = z.object({
  itinerary: itinerarySchema,
  highlights: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  pickupInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  included: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  excluded: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  scheduleInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  accessInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notSuitable: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  notAllowed: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  whatToBring: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  infoBeforeBooking: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  knowBeforeYouGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type EventBlocks = z.infer<typeof eventBlocksSchema>;

export type BlockFieldType = "localizedStringList" | "itinerary" | "whyGo";

export interface BlockFieldMeta {
  /** key inside the per-template schema (e.g. "included") */
  key: string;
  /** Remix icon class (e.g. "ri-check-line") for renderers + admin UI */
  icon: string;
  /** i18n key for the block heading; admin/web both look this up */
  titleKey: string;
  /** Tailwind/css color hint for icon tinting on the web side */
  tone: "positive" | "negative" | "neutral";
  /**
   * Drives which editor (admin) and renderer (web) handle this block.
   * Defaults to "localizedStringList" for backwards-compat with the
   * MVP `experience` template.
   */
  fieldType?: BlockFieldType;
}

export interface TemplateDefinition {
  /** Stable id stored in `categories.template`. */
  id: string;
  /** Zod schema validating `activities.contentBlocks` for this template. */
  schema: z.ZodTypeAny;
  /** Ordered list of fields the admin renders + the web displays. */
  blocks: BlockFieldMeta[];
  /** i18n key for the human-friendly template label (admin select). */
  labelKey: string;
}

const EXPERIENCE_LIST_BLOCKS: BlockFieldMeta[] = [
  {
    key: "highlights",
    icon: "ri-sparkling-line",
    titleKey: "blocks.highlights",
    tone: "neutral",
    fieldType: "localizedStringList",
  },
  {
    key: "pickupInfo",
    icon: "ri-bus-line",
    titleKey: "blocks.pickup_info",
    tone: "neutral",
    fieldType: "localizedStringList",
  },
  {
    key: "included",
    icon: "ri-checkbox-circle-line",
    titleKey: "blocks.included",
    tone: "positive",
    fieldType: "localizedStringList",
  },
  {
    key: "excluded",
    icon: "ri-close-circle-line",
    titleKey: "blocks.excluded",
    tone: "negative",
    fieldType: "localizedStringList",
  },
  {
    key: "notSuitable",
    icon: "ri-alert-line",
    titleKey: "blocks.not_suitable",
    tone: "negative",
    fieldType: "localizedStringList",
  },
  {
    key: "notAllowed",
    icon: "ri-forbid-2-line",
    titleKey: "blocks.not_allowed",
    tone: "negative",
    fieldType: "localizedStringList",
  },
  {
    key: "whatToBring",
    icon: "ri-suitcase-line",
    titleKey: "blocks.what_to_bring",
    tone: "neutral",
    fieldType: "localizedStringList",
  },
  {
    key: "infoBeforeBooking",
    icon: "ri-information-line",
    titleKey: "blocks.info_before_booking",
    tone: "neutral",
    fieldType: "localizedStringList",
  },
  {
    key: "knowBeforeYouGo",
    icon: "ri-lightbulb-line",
    titleKey: "blocks.know_before_you_go",
    tone: "neutral",
    fieldType: "localizedStringList",
  },
];

const STAY_EXTRA_BLOCKS: BlockFieldMeta[] = [
  { key: "propertyDetails", icon: "ri-hotel-line", titleKey: "blocks.property_details", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestFor", icon: "ri-group-line", titleKey: "blocks.best_for", tone: "positive", fieldType: "localizedStringList" },
  { key: "amenities", icon: "ri-hotel-bed-line", titleKey: "blocks.amenities", tone: "positive", fieldType: "localizedStringList" },
  { key: "roomFeatures", icon: "ri-door-open-line", titleKey: "blocks.room_features", tone: "neutral", fieldType: "localizedStringList" },
  { key: "services", icon: "ri-customer-service-line", titleKey: "blocks.services", tone: "positive", fieldType: "localizedStringList" },
  { key: "safety", icon: "ri-shield-check-line", titleKey: "blocks.safety", tone: "positive", fieldType: "localizedStringList" },
  { key: "accessibility", icon: "ri-accessible-line", titleKey: "blocks.accessibility", tone: "neutral", fieldType: "localizedStringList" },
  { key: "checkInOut", icon: "ri-login-box-line", titleKey: "blocks.check_in_out", tone: "neutral", fieldType: "localizedStringList" },
  { key: "houseRules", icon: "ri-file-shield-2-line", titleKey: "blocks.house_rules", tone: "neutral", fieldType: "localizedStringList" },
];

const MENU_HIGHLIGHTS_BLOCK: BlockFieldMeta = {
  key: "menuHighlights",
  icon: "ri-restaurant-2-line",
  titleKey: "blocks.menu_highlights",
  tone: "positive",
  fieldType: "localizedStringList",
};

/**
 * Restaurants, activities, spas, pools and trips (owner, 2026-10-28): shown
 * as the pewter panel under the title, in place of the Highlights list.
 */
const WHY_GO_BLOCK: BlockFieldMeta = {
  key: "whyGo",
  icon: "ri-sparkling-2-line",
  titleKey: "blocks.why_go",
  tone: "positive",
  fieldType: "whyGo",
};

const RESTAURANT_EXTRA_BLOCKS: BlockFieldMeta[] = [
  MENU_HIGHLIGHTS_BLOCK,
  { key: "dietaryInfo", icon: "ri-leaf-line", titleKey: "blocks.dietary_info", tone: "neutral", fieldType: "localizedStringList" },
  { key: "seatingInfo", icon: "ri-reserved-line", titleKey: "blocks.seating_info", tone: "neutral", fieldType: "localizedStringList" },
];

const EVENT_EXTRA_BLOCKS: BlockFieldMeta[] = [
  { key: "scheduleInfo", icon: "ri-calendar-schedule-line", titleKey: "blocks.schedule_info", tone: "neutral", fieldType: "localizedStringList" },
  { key: "accessInfo", icon: "ri-ticket-2-line", titleKey: "blocks.access_info", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_LOCATION_BLOCKS: BlockFieldMeta[] = [
  { key: "whyVisit", icon: "ri-sparkling-line", titleKey: "blocks.why_visit", tone: "positive", fieldType: "localizedStringList" },
  { key: "whatToExpect", icon: "ri-eye-line", titleKey: "blocks.what_to_expect", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestTime", icon: "ri-time-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "howToCombine", icon: "ri-route-line", titleKey: "blocks.how_to_combine", tone: "neutral", fieldType: "localizedStringList" },
  { key: "openingHours", icon: "ri-calendar-2-line", titleKey: "blocks.opening_hours", tone: "neutral", fieldType: "localizedStringList" },
  { key: "priceRange", icon: "ri-price-tag-3-line", titleKey: "blocks.price_range", tone: "neutral", fieldType: "localizedStringList" },
  { key: "practicalTips", icon: "ri-lightbulb-line", titleKey: "blocks.practical_tips", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_RESTAURANT_BLOCKS: BlockFieldMeta[] = [
  { key: "whyEat", icon: "ri-sparkling-line", titleKey: "blocks.why_eat", tone: "positive", fieldType: "localizedStringList" },
  { key: "menuHighlights", icon: "ri-restaurant-2-line", titleKey: "blocks.menu_highlights", tone: "positive", fieldType: "localizedStringList" },
  { key: "cuisine", icon: "ri-leaf-line", titleKey: "blocks.cuisine", tone: "neutral", fieldType: "localizedStringList" },
  { key: "priceRange", icon: "ri-money-dollar-circle-line", titleKey: "blocks.price_range", tone: "neutral", fieldType: "localizedStringList" },
  { key: "openingHours", icon: "ri-calendar-2-line", titleKey: "blocks.opening_hours", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestTime", icon: "ri-time-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "howToCombine", icon: "ri-route-line", titleKey: "blocks.how_to_combine", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_NIGHTLIFE_BLOCKS: BlockFieldMeta[] = [
  { key: "whyGo", icon: "ri-sparkling-line", titleKey: "blocks.why_go", tone: "positive", fieldType: "localizedStringList" },
  { key: "musicAndVibe", icon: "ri-music-2-line", titleKey: "blocks.music_and_vibe", tone: "positive", fieldType: "localizedStringList" },
  { key: "bestNights", icon: "ri-calendar-event-line", titleKey: "blocks.best_nights", tone: "neutral", fieldType: "localizedStringList" },
  { key: "openingHours", icon: "ri-time-line", titleKey: "blocks.opening_hours", tone: "neutral", fieldType: "localizedStringList" },
  { key: "gettingIn", icon: "ri-door-open-line", titleKey: "blocks.getting_in", tone: "neutral", fieldType: "localizedStringList" },
  { key: "priceRange", icon: "ri-money-dollar-circle-line", titleKey: "blocks.price_range", tone: "neutral", fieldType: "localizedStringList" },
  { key: "goodToKnow", icon: "ri-information-line", titleKey: "blocks.good_to_know", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_BEACH_CLUB_BLOCKS: BlockFieldMeta[] = [
  { key: "whyGo", icon: "ri-sparkling-line", titleKey: "blocks.why_go", tone: "positive", fieldType: "localizedStringList" },
  { key: "whatToExpect", icon: "ri-eye-line", titleKey: "blocks.what_to_expect", tone: "neutral", fieldType: "localizedStringList" },
  { key: "dayPass", icon: "ri-ticket-2-line", titleKey: "blocks.day_pass", tone: "neutral", fieldType: "localizedStringList" },
  { key: "foodAndDrink", icon: "ri-restaurant-2-line", titleKey: "blocks.food_and_drink", tone: "positive", fieldType: "localizedStringList" },
  { key: "openingHours", icon: "ri-time-line", titleKey: "blocks.opening_hours", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestTime", icon: "ri-sun-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "goodToKnow", icon: "ri-information-line", titleKey: "blocks.good_to_know", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_STAY_BLOCKS: BlockFieldMeta[] = [
  { key: "whyStay", icon: "ri-sparkling-line", titleKey: "blocks.why_stay", tone: "positive", fieldType: "localizedStringList" },
  { key: "whatToExpect", icon: "ri-eye-line", titleKey: "blocks.what_to_expect", tone: "neutral", fieldType: "localizedStringList" },
  { key: "amenities", icon: "ri-hotel-bed-line", titleKey: "blocks.amenities", tone: "positive", fieldType: "localizedStringList" },
  { key: "priceRange", icon: "ri-money-dollar-circle-line", titleKey: "blocks.price_range", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestTime", icon: "ri-time-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "nearby", icon: "ri-map-pin-line", titleKey: "blocks.nearby", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_SPA_BLOCKS: BlockFieldMeta[] = [
  { key: "whyBook", icon: "ri-sparkling-line", titleKey: "blocks.why_book", tone: "positive", fieldType: "localizedStringList" },
  { key: "treatments", icon: "ri-flower-line", titleKey: "blocks.treatments", tone: "positive", fieldType: "localizedStringList" },
  { key: "priceRange", icon: "ri-money-dollar-circle-line", titleKey: "blocks.price_range", tone: "neutral", fieldType: "localizedStringList" },
  { key: "openingHours", icon: "ri-calendar-2-line", titleKey: "blocks.opening_hours", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestTime", icon: "ri-time-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "howToCombine", icon: "ri-route-line", titleKey: "blocks.how_to_combine", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_GUIDE_BLOCKS: BlockFieldMeta[] = [
  { key: "overview", icon: "ri-book-open-line", titleKey: "blocks.overview", tone: "neutral", fieldType: "localizedStringList" },
  { key: "stops", icon: "ri-map-pin-line", titleKey: "blocks.stops", tone: "neutral", fieldType: "itinerary" },
  { key: "bestTime", icon: "ri-time-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "practicalTips", icon: "ri-lightbulb-line", titleKey: "blocks.practical_tips", tone: "neutral", fieldType: "localizedStringList" },
  { key: "howToCombine", icon: "ri-route-line", titleKey: "blocks.how_to_combine", tone: "neutral", fieldType: "localizedStringList" },
];

const EXPLORE_GOLF_BLOCKS: BlockFieldMeta[] = [
  { key: "whyPlay", icon: "ri-golf-ball-line", titleKey: "blocks.why_play", tone: "positive", fieldType: "localizedStringList" },
  { key: "courseDetails", icon: "ri-flag-line", titleKey: "blocks.course_details", tone: "neutral", fieldType: "localizedStringList" },
  { key: "facilities", icon: "ri-building-line", titleKey: "blocks.facilities", tone: "positive", fieldType: "localizedStringList" },
  { key: "openingHours", icon: "ri-calendar-2-line", titleKey: "blocks.opening_hours", tone: "neutral", fieldType: "localizedStringList" },
  { key: "bestTime", icon: "ri-time-line", titleKey: "blocks.best_time", tone: "neutral", fieldType: "localizedStringList" },
  { key: "practicalTips", icon: "ri-lightbulb-line", titleKey: "blocks.practical_tips", tone: "neutral", fieldType: "localizedStringList" },
  { key: "howToCombine", icon: "ri-route-line", titleKey: "blocks.how_to_combine", tone: "neutral", fieldType: "localizedStringList" },
];

/**
 * `transfer` template: private rides sold per vehicle (airport, intercity,
 * evenings, driver by the hour). The booking form, not these blocks, carries
 * the product; the blocks are the short "good to know" under it.
 */
export const transferBlocksSchema = z.object({
  pickupInfo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  included: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
  knowBeforeYouGo: localizedStringListSchema.default({ en: [], fr: [], ar: [] }),
});
export type TransferBlocks = z.infer<typeof transferBlocksSchema>;

const TRANSFER_BLOCK_ORDER = ["pickupInfo", "included", "knowBeforeYouGo"];
const TRANSFER_BLOCKS: BlockFieldMeta[] = TRANSFER_BLOCK_ORDER.map(
  (key) => EXPERIENCE_LIST_BLOCKS.find((b) => b.key === key)!,
);

export const templateRegistry: Record<string, TemplateDefinition> = {
  experience: {
    id: "experience",
    schema: experienceBlocksSchema,
    labelKey: "templates.experience",
    // Menu sits right after included/excluded, same slot the `restaurant`
    // template uses, so a dinner show reads like a restaurant page there.
    blocks: [
      WHY_GO_BLOCK,
      ...EXPERIENCE_LIST_BLOCKS.slice(0, 4),
      MENU_HIGHLIGHTS_BLOCK,
      ...EXPERIENCE_LIST_BLOCKS.slice(4),
    ],
  },
  trip: {
    id: "trip",
    schema: tripBlocksSchema,
    labelKey: "templates.trip",
    blocks: [
      {
        key: "itinerary",
        icon: "ri-route-line",
        titleKey: "blocks.itinerary",
        tone: "neutral",
        fieldType: "itinerary",
      },
      WHY_GO_BLOCK,
      ...EXPERIENCE_LIST_BLOCKS,
    ],
  },
  stay: {
    id: "stay",
    schema: stayBlocksSchema,
    labelKey: "templates.stay",
    blocks: [
      ...EXPERIENCE_LIST_BLOCKS.slice(0, 4),
      ...STAY_EXTRA_BLOCKS,
      ...EXPERIENCE_LIST_BLOCKS.slice(4),
    ],
  },
  restaurant: {
    id: "restaurant",
    schema: restaurantBlocksSchema,
    labelKey: "templates.restaurant",
    blocks: [
      WHY_GO_BLOCK,
      ...EXPERIENCE_LIST_BLOCKS.slice(0, 4),
      ...RESTAURANT_EXTRA_BLOCKS,
      ...EXPERIENCE_LIST_BLOCKS.slice(4),
    ],
  },
  event: {
    id: "event",
    schema: eventBlocksSchema,
    labelKey: "templates.event",
    blocks: [
      {
        key: "itinerary",
        icon: "ri-route-line",
        titleKey: "blocks.itinerary",
        tone: "neutral",
        fieldType: "itinerary",
      },
      ...EXPERIENCE_LIST_BLOCKS.slice(0, 4),
      ...EVENT_EXTRA_BLOCKS,
      ...EXPERIENCE_LIST_BLOCKS.slice(4),
    ],
  },
  "explore-location": {
    id: "explore-location",
    schema: exploreLocationBlocksSchema,
    labelKey: "templates.explore_location",
    blocks: EXPLORE_LOCATION_BLOCKS,
  },
  "explore-restaurant": {
    id: "explore-restaurant",
    schema: exploreRestaurantBlocksSchema,
    labelKey: "templates.explore_restaurant",
    blocks: EXPLORE_RESTAURANT_BLOCKS,
  },
  "explore-nightlife": {
    id: "explore-nightlife",
    schema: exploreNightlifeBlocksSchema,
    labelKey: "templates.explore_nightlife",
    blocks: EXPLORE_NIGHTLIFE_BLOCKS,
  },
  "explore-beach-club": {
    id: "explore-beach-club",
    schema: exploreBeachClubBlocksSchema,
    labelKey: "templates.explore_beach_club",
    blocks: EXPLORE_BEACH_CLUB_BLOCKS,
  },
  "explore-stay": {
    id: "explore-stay",
    schema: exploreStayBlocksSchema,
    labelKey: "templates.explore_stay",
    blocks: EXPLORE_STAY_BLOCKS,
  },
  "explore-spa": {
    id: "explore-spa",
    schema: exploreSpaBlocksSchema,
    labelKey: "templates.explore_spa",
    blocks: EXPLORE_SPA_BLOCKS,
  },
  "explore-guide": {
    id: "explore-guide",
    schema: exploreGuideBlocksSchema,
    labelKey: "templates.explore_guide",
    blocks: EXPLORE_GUIDE_BLOCKS,
  },
  "explore-golf": {
    id: "explore-golf",
    schema: exploreGolfBlocksSchema,
    labelKey: "templates.explore_golf",
    blocks: EXPLORE_GOLF_BLOCKS,
  },
  transfer: {
    id: "transfer",
    schema: transferBlocksSchema,
    labelKey: "templates.transfer",
    blocks: TRANSFER_BLOCKS,
  },
};

/** Maps `activities.segment` (when mode='explore') → template id. */
export const EXPLORE_TEMPLATE_BY_SEGMENT: Record<string, string> = {
  locations: "explore-location",
  restaurants: "explore-restaurant",
  stays: "explore-stay",
  spas: "explore-spa",
  guides: "explore-guide",
  golf: "explore-golf",
  nightlife: "explore-nightlife",
  "beach-clubs": "explore-beach-club",
};

const DEFAULT_TEMPLATE_BY_CATEGORY_SLUG: Record<string, keyof typeof templateRegistry> = {
  activities: "experience",
  air: "experience",
  boats: "experience",
  buggy: "experience",
  camel: "experience",
  diving: "experience",
  horse: "experience",
  "horse--camel": "experience",
  "jet-ski": "experience",
  pools: "experience",
  quad: "experience",
  shows: "experience",
  spas: "experience",
  water: "experience",
  workshops: "experience",
  "city-tour": "trip",
  "day-trips": "trip",
  excursions: "trip",
  camp: "stay",
  stays: "stay",
  villa: "stay",
  restaurants: "restaurant",
  festivals: "event",
  trips: "trip",
  transport: "transfer",
};

/**
 * Whether a page shows the "Why people go" rows: every template with a
 * `whyGo` rows block (restaurant, experience, trip), plus explore restaurants,
 * which have shown them since the panel shipped. Not the nightlife and beach
 * club templates: their `whyGo` is a plain string list, not rows.
 */
export function showsWhyGoPanel(template: string | null | undefined): boolean {
  if (!template) return false;
  if (template === "explore-restaurant") return true;
  return (templateRegistry[template]?.blocks ?? []).some((b) => b.key === "whyGo" && b.fieldType === "whyGo");
}

export function inferCategoryTemplate(
  template: string | null | undefined,
  slug: string | null | undefined,
  opts?: { mode?: string | null; segment?: string | null },
): string | null {
  // Explore-mode rows resolve template from segment (independent of category).
  if (opts?.mode === "explore" && opts.segment) {
    const exploreTemplate = EXPLORE_TEMPLATE_BY_SEGMENT[opts.segment];
    if (exploreTemplate) return exploreTemplate;
  }
  if (template && template in templateRegistry) return template;
  if (!slug) return null;
  return DEFAULT_TEMPLATE_BY_CATEGORY_SLUG[slug] ?? null;
}

/**
 * Returns the schema validating `contentBlocks` for the given template id, or
 * `null` if the category has no template (in which case callers should treat
 * `contentBlocks` as opaque and discard it).
 */
export function pickBlocksSchema(template: string | null | undefined): z.ZodTypeAny | null {
  if (!template) return null;
  return templateRegistry[template]?.schema ?? null;
}

/** Allowed template ids — useful for admin selects + zod enums. */
export function listTemplateIds(): string[] {
  return Object.keys(templateRegistry);
}

/** Discriminated union of every template's payload. */
export type ActivityContentBlocks =
  | ExperienceBlocks
  | TripBlocks
  | StayBlocks
  | RestaurantBlocks
  | EventBlocks
  | ExploreLocationBlocks
  | ExploreRestaurantBlocks
  | ExploreStayBlocks
  | ExploreSpaBlocks
  | ExploreGuideBlocks
  | ExploreGolfBlocks
  | TransferBlocks
  | Record<string, never>;
